import { type FC } from 'react';
import { useSelector } from 'react-redux';
import { difference } from 'lodash-es';

import { type RootState } from '@editor2/reducers/root';
import { chapterHeader } from './styles';

import ChapterChangesDiff from '@editor2/components/CourseChangesDiff/diff/ChapterChangesDiff';
import LectureSlideDeckChangesDiff from '@editor2/components/CourseChangesDiff/diff/LectureSlideDeckChangesDiff';
import { getChapterTitle } from '@editor2/components/CourseChangesDiff/helpers';
import ChangesDiff from '@editor2/components/CourseChangesDiff/common/ChangesDiff';
import DeletedItem from '@editor2/components/CourseChangesDiff/common/DeletedItem';

import { type LectureSlideDeck } from '@editor2/types/lectureSlideDecks';
import {
	type BookPayload as UnsavedBookChanges,
	type ChapterPayload,
	type LectureSlideDeckPayload
} from '@editor2/types/unsavedChangesPayload';
import { type Chapter } from '@editor2/types/chapter';

interface Props {
	bookChanges: UnsavedBookChanges;
	courseId: number;
}

const CourseChangesDiff: FC<Props> = (props) => {
	const { bookChanges, courseId } = props;

	const { initialCourse, book } = useSelector((state: RootState) => state.courses[courseId]);
	const { chapters, lecture_slide_decks, ...courseChanges } = bookChanges;

	const getInitialChapterById = (id: number) => {
		return Object.values(initialCourse.chapters.entities).find(
			(element: Chapter) => element.id === id
		);
	};

	const getChangesChapterIds = () => {
		return (
			bookChanges.chapters?.map(
				(entity: ChapterPayload) => entity.family_id || getInitialChapterById(entity.id!)!.family_id
			) || []
		);
	};

	const getChangesDeckFamilyIds = () => {
		return (
			bookChanges.lecture_slide_decks?.map((changedDeck: LectureSlideDeckPayload) => {
				if (changedDeck.family_id) return changedDeck.family_id;
				const initial = Object.values(initialCourse.lecture_slide_decks?.entities ?? {}).find(
					(deck) => deck?.id === changedDeck.id
				);
				return initial?.family_id;
			}) ?? []
		);
	};

	const getDeckTitle = (deck: LectureSlideDeck) => {
		const name = deck?.file?.original_name;
		return name ? `File: ${name}` : 'Lecture Slide Deck';
	};

	return (
		<>
			<ChangesDiff
				index={0}
				initialIndex={0}
				initial={initialCourse.book}
				attributeChanges={courseChanges}
				title={book.name}
			/>

			{bookChanges.chapters?.map((chapterObj, index) => (
				<ChapterChangesDiff
					courseId={courseId}
					chapterChanges={chapterObj}
					index={index}
					key={index}
				/>
			))}

			{bookChanges.chapters &&
				difference(initialCourse.chapters.ids, getChangesChapterIds()).map((family_id) => {
					return (
						<DeletedItem
							key={family_id}
							title={getChapterTitle(initialCourse.chapters.entities[family_id!])}
							styles={chapterHeader}
						/>
					);
				})}

			{bookChanges.lecture_slide_decks?.map((deckObj, index) => (
				<LectureSlideDeckChangesDiff
					courseId={courseId}
					deckChanges={deckObj}
					index={index}
					key={deckObj.family_id ?? deckObj.id}
				/>
			))}

			{bookChanges.lecture_slide_decks &&
				difference(initialCourse.lecture_slide_decks?.ids ?? [], getChangesDeckFamilyIds()).map(
					(family_id) => (
						<DeletedItem
							key={family_id}
							title={getDeckTitle(initialCourse.lecture_slide_decks?.entities[family_id!])}
							styles={chapterHeader}
						/>
					)
				)}
		</>
	);
};

export default CourseChangesDiff;
